import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Accordion as AccordionStyles } from '@airbus/elements/elements'
import { Add, Remove } from '@mui/icons-material'
import { createUseStyles } from '../../lib/styling'
import useControlled from '../../lib/hooks/useControlled'
import { CommonProps } from '../helperTypes'
import Button from '../Button'

type Props = PropsWithChildren<{
  /** If `true`, only one section can be expanded at any time */
  single?: boolean
  /** Initial index(es) of the expanded accordion item(s) */
  defaultIndex?: number | number[]
  /** Index(es) of the expanded accordion item */
  index?: number | number[]
  /** Callback invoked when accordion items are expanded or collapsed */
  onChange?: (index: number | number[]) => void
}>

export type AccordionProps = Props & CommonProps

const useStyles = createUseStyles(AccordionStyles, 'accordion')

const checkProps = ({ defaultIndex, index, single }: AccordionProps): void => {
  if (process.env.NODE_ENV === 'production') return
  if (!single) {
    if ((index && !Array.isArray(index)) || (defaultIndex && !Array.isArray(defaultIndex))) {
      console.warn(
        [
          '[AIRBUS-DS] The Accordion expects `index` and `defaultIndex` to be arrays',
          'when used without the `single` prop',
        ].join('\n'),
      )
    }
  } else {
    if (Array.isArray(index) || Array.isArray(defaultIndex)) {
      console.warn(
        [
          '[AIRBUS-DS] The Accordion expects `index` and `defaultIndex` to be numbers',
          'when used with the `single` prop',
        ].join('\n'),
      )
    }
  }
}

const Accordion = (props: AccordionProps) => {
  const { children, className, defaultIndex, index: indexProp, onChange, single, ...rest } = props
  React.useEffect(() => checkProps(props), [props])
  const classes = useStyles()

  // controlled / uncontrolled value
  const [index, setIndex] = useControlled({
    value: indexProp,
    defaultValue: defaultIndex,
  })

  const sectionCount = React.useMemo(() => {
    let count = 0
    React.Children.forEach(children, (child: any) => (count += child.props.disabled ? 0 : 1))
    return count
  }, [children])

  const handleChange = React.useCallback(
    (changedIndex: number, expanded: boolean): void => {
      let newIndex: number | number[] = changedIndex
      if (!expanded) {
        if (single) {
          newIndex = -1
        } else {
          const removeIndex = index.indexOf(changedIndex)
          newIndex = [...index.slice(0, removeIndex), ...index.slice(removeIndex + 1)]
        }
      } else if (!single) {
        newIndex = index ? [...index, changedIndex] : [changedIndex]
      }
      setIndex(newIndex)
      onChange && onChange(newIndex)
    },
    [index, onChange, setIndex, single],
  )

  const sections = React.useMemo(() => {
    return React.Children.map(children, (child, childIndex) => {
      if (!React.isValidElement(child)) {
        return null
      }

      if (child.type === React.Fragment) {
        console.warn(
          "[AIRBUS-DS] The Accordion component doesn't accept a Fragment as a child. Consider providing an array instead.",
        )
      }

      const { expanded: childExpanded, onChange: childOnChange, ...childProps } = child.props
      const expanded = index === childIndex || (Array.isArray(index) && index.indexOf(childIndex) >= 0)
      return React.cloneElement<any>(child, {
        expanded,
        onChange: (evt: any, expanded: boolean) => handleChange(childIndex, expanded),
        ...childProps,
      })
    })
  }, [children, handleChange, index])

  const disableCollapseAll = React.useMemo(() => {
    if (single) return index === undefined || index === -1
    else return index === undefined || !Array.isArray(index) || index.length === 0
  }, [index, single])

  const disableUnfoldAll = React.useMemo(() => {
    if (single) return index >= 0
    else return Array.isArray(index) && index.length === sectionCount
  }, [index, sectionCount, single])

  const collapseAll = () => {
    const newIndex = single ? -1 : []
    setIndex(newIndex)
    onChange && onChange(newIndex)
  }

  const unfoldAll = () => {
    const newIndex = React.Children.map(children, (child, childIndex) => childIndex)
    if (!newIndex) return
    setIndex(newIndex)
    onChange && onChange(newIndex)
  }

  return (
    <div className={clsx(className, classes.root)} {...rest}>
      <div className={classes.actions}>
        <Button disabled={disableCollapseAll} variant='ghost' size='xsmall' icon={<Remove />} onClick={collapseAll}>
          Collapse all
        </Button>
        {!single && (
          <Button disabled={disableUnfoldAll} variant='ghost' size='xsmall' icon={<Add />} onClick={unfoldAll}>
            Unfold all
          </Button>
        )}
      </div>
      {sections}
    </div>
  )
}

export default Accordion
